import React, { useEffect, useState, useMemo } from 'react';
import Table, { ColumnConfig } from 'components/table';
import Pagination from 'components/pagination';
import Search from 'components/search';
import ExportBtn from 'components/modals/export/exportBtn';
import ExportModal from 'components/modals/export';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Icon } from 'components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePayment from 'state/payment/hooks/usePayment';
import useInvoice, {
  PaymentStatusFilter,
  IInvoiceData,
  getStatusesForFilter,
} from 'state/payment/hooks/useInvoice';
import useQuery from 'utils/hooks/useQuery';
import CURRENCY from 'utils/currency';
import { formatToTz } from 'utils/date-format';
import { apiWithOrg } from 'services/api';
import Filter from 'components/filter';
import { EFilterConfigType, IFilterConfig } from 'components/filter/types';
import ToastContainer, { ToastType } from 'components/toasts/container';
import { cn } from 'lib/utils';

import { InvoiceStatus, isInvoicePaid } from 'state/payment/hooks/useInvoice';

// Helper function to normalize payment method display
const normalizePaymentMethodDisplay = (
  method: string | null | undefined
): string => {
  if (!method) return 'ACH';

  const methodLower = method.toLowerCase();

  // Handle special cases and proper capitalization
  switch (methodLower) {
    case 'ach':
      return 'ACH';
    case 'atm':
      return 'CATM'; // Custom ATM display
    case 'credit':
      return 'Credit';
    case 'debit':
      return 'Debit';
    default:
      // Capitalize first letter for unknown methods
      return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
  }
};

// Status formatter using Badge components like payments detail tab
const invoiceStatusFormatter = (status: string): JSX.Element => {
  switch (status?.toLowerCase()) {
    case 'new':
    case 'created':
      return <Badge variant="info">Created</Badge>;
    case 'submitted':
      return <Badge variant="info">Submitted</Badge>;
    case 'captured':
      return <Badge variant="success">Captured</Badge>;
    case 'canceled':
    case 'cancelled':
      return <Badge variant="disabled">Canceled</Badge>;
    case 'error':
    case 'failed':
      return <Badge variant="danger">Error</Badge>;
    case 'voided':
      return <Badge variant="disabled">Voided</Badge>;
    default:
      return <Badge variant="info">{status || 'Unknown'}</Badge>;
  }
};

interface IProps {
  dateFrom: string;
  dateTo: string;
  triggerRefresh: boolean;
  isActiveTab: boolean;
}

const InvoicesTab = ({
  dateFrom,
  dateTo,
  triggerRefresh,
  isActiveTab,
}: IProps): JSX.Element => {
  const { organisation } = useOrganisation();
  const { settings } = organisation;
  const {
    toChargeCapture,
    toChargeCancel,
    toFetchInvoiceCSV,
    payment,
    toClearTransactionsExport,
  } = usePayment();
  const { url, exportResponse, error: paymentError } = payment;

  const urlParams = useQuery(location?.search);

  // Use custom invoice hook
  const {
    invoices,
    loading,
    error,
    numResults,
    prevKey,
    nextKey,
    fetchInvoices,
    resetInvoices,
  } = useInvoice();

  const searchString = urlParams.get('search') || undefined;

  // Payment status filter state - default to unpaid
  const [paymentStatusFilter, setPaymentStatusFilter] =
    useState<PaymentStatusFilter>(PaymentStatusFilter.UNPAID);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<ToastType>(ToastType.success);
  const [showToast, setShowToast] = useState(false);

  // Filter state from URL parameters
  const amountOption = urlParams.get('condition') || 'equal';
  const firstAmount = urlParams.get('amount1') || undefined;
  const statusArray = useMemo(
    () => urlParams.get('status')?.split(',') || [],
    [urlParams.get('status')]
  );

  // Effect to fetch data when component mounts or dependencies change
  useEffect(() => {
    if (isActiveTab) {
      fetchInvoices(
        {
          start_date: dateFrom,
          end_date: dateTo,
          search_string: searchString,
          paymentStatusFilter,
          status_filter: statusArray,
          // Include amount filtering parameters from URL
          amount_1: firstAmount ? parseFloat(firstAmount) : undefined,
          condition: firstAmount ? (amountOption as any) : undefined,
        },
        isActiveTab
      );
    }
  }, [
    dateFrom,
    dateTo,
    triggerRefresh,
    isActiveTab,
    searchString,
    paymentStatusFilter,
    statusArray,
    firstAmount,
    amountOption,
  ]);

  // Pagination handlers
  const prevPage = () => {
    fetchInvoices(
      {
        start_date: dateFrom,
        end_date: dateTo,
        search_string: searchString,
        key: prevKey,
        paymentStatusFilter,
        status_filter: statusArray,
        // Include amount filtering parameters
        amount_1: firstAmount ? parseFloat(firstAmount) : undefined,
        condition: firstAmount ? (amountOption as any) : undefined,
      },
      isActiveTab
    );
  };

  const nextPage = () => {
    fetchInvoices(
      {
        start_date: dateFrom,
        end_date: dateTo,
        search_string: searchString,
        key: nextKey,
        paymentStatusFilter,
        status_filter: statusArray,
        // Include amount filtering parameters
        amount_1: firstAmount ? parseFloat(firstAmount) : undefined,
        condition: firstAmount ? (amountOption as any) : undefined,
      },
      isActiveTab
    );
  };

  // Calculate gross amount (original_amount + fee_amount)
  const getGrossAmount = (row: IInvoiceData): number => {
    return row.original_amount + row.fee_amount;
  };

  // Helper function to show toast notifications
  const showToastNotification = (
    message: string,
    type: ToastType = ToastType.success
  ) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // Helper function to refresh invoice data after operations
  const refreshInvoices = () => {
    fetchInvoices(
      {
        start_date: dateFrom,
        end_date: dateTo,
        search_string: searchString,
        paymentStatusFilter,
        status_filter: statusArray,
        // Include amount filtering parameters
        amount_1: firstAmount ? parseFloat(firstAmount) : undefined,
        condition: firstAmount ? (amountOption as any) : undefined,
      },
      isActiveTab
    );
  };

  // Handle tab switching with filter clearing logic
  const handleTabSwitch = (newTab: PaymentStatusFilter) => {
    setPaymentStatusFilter(newTab);

    // Clear only status filter when switching to Paid tab since only captured invoices exist
    // Keep amount filters as they're still useful for filtering paid invoices by amount
    if (newTab === PaymentStatusFilter.PAID) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('status'); // Only remove status filter
      window.history.replaceState({}, '', newUrl.toString());
    }
  };

  // Function to fetch ticket details from HintsService
  const fetchTicketDetails = async (
    ticketAlphaNumId: string
  ): Promise<string | null> => {
    try {
      if (!ticketAlphaNumId) {
        return null;
      }
      // Check if ticket_alpha_num_id is 7 characters - generate UUID directly
      if (ticketAlphaNumId.length === 7) {
        const uuid = crypto.randomUUID();
        console.log(
          `Generated UUID for 7-char ticket ${ticketAlphaNumId}: ${uuid}`
        );
        return uuid;
      }

      // For non-7-char tickets, call HintsService API
      const baseUrl = organisation.activeOrg?.settings.sell_treez_url;
      const serviceUrl =
        baseUrl?.startsWith('http://') || baseUrl?.startsWith('https://')
          ? baseUrl
          : `https://${baseUrl}`;

      const response = await apiWithOrg.get<{
        ticketList: Array<{ ticket_id: string }>;
      }>(
        `${serviceUrl}/HintsService/v1.0/rest/ticket/external/ticket/ordernumber/${ticketAlphaNumId}`
      );

      if (response.data.ticketList && response.data.ticketList.length > 0) {
        return response.data.ticketList[0].ticket_id;
      }

      throw new Error(`No ticket found for ${ticketAlphaNumId}`);
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      throw err;
    }
  };

  // Action handler for Cancel Invoice
  const handleCancelInvoice = async (invoice: IInvoiceData) => {
    try {
      // Fetch ticket_id from HintsService
      const ticketId = await fetchTicketDetails(invoice.ticket_alpha_num_id);

      const cancelRequest = {
        invoiceId: invoice.gateway_invoice_id,
        processorId: invoice.processor_id,
        paymentChargeId: invoice.charge_id,
        ticketAlphaNumId: invoice.ticket_alpha_num_id,
        ticketId: ticketId,
        originalAmount: invoice.original_amount,
        feeAmount: invoice.fee_amount,
        referenceNumber: invoice.reference_number,
        employeeReferenceId: invoice.associate_id,
        isSync: true,
      };

      const success = await toChargeCancel(cancelRequest);

      if (success) {
        showToastNotification('Invoice cancelled successfully');
        refreshInvoices();
      } else {
        showToastNotification('Failed to cancel invoice', ToastType.error);
      }
    } catch (err) {
      console.error('Cancel invoice error:', err);
      showToastNotification(
        'An unexpected error occurred while cancelling the invoice',
        ToastType.error
      );
    }
  };

  // Action handler for Capture Invoice
  const handleCaptureInvoice = async (invoice: IInvoiceData) => {
    try {
      // Fetch ticket_id from HintsService
      const ticketId = await fetchTicketDetails(invoice.ticket_alpha_num_id);

      const captureRequest = {
        invoiceId: invoice.gateway_invoice_id,
        processorId: invoice.processor_id,
        paymentChargeId: invoice.charge_id,
        ticketAlphaNumId: invoice.ticket_alpha_num_id,
        ticketId: ticketId,
        originalAmount: invoice.original_amount,
        feeAmount: invoice.fee_amount,
        referenceNumber: invoice.reference_number,
        employeeReferenceId: invoice.associate_id,
        isSync: true,
      };

      const success = await toChargeCapture(captureRequest);

      if (success) {
        showToastNotification('Invoice captured successfully');
        refreshInvoices();
      } else {
        showToastNotification('Failed to capture invoice', ToastType.error);
      }
    } catch (err) {
      console.error('Capture invoice error:', err);
      showToastNotification(
        'An unexpected error occurred while capturing the invoice',
        ToastType.error
      );
    }
  };

  // Export handler that applies consistent filtering with main invoice view
  const handleInvoiceExport = (params: any) => {
    const exportParams = {
      ...params,
      method: 'ACH,ach', // Filter for ACH invoices (consistent with main view)
    };

    // Apply smart status filtering based on current paid/unpaid tab
    // If specific status filter is provided via export modal, that takes precedence
    if (!params.status) {
      exportParams.status = getStatusesForFilter(paymentStatusFilter);
    }

    toFetchInvoiceCSV(exportParams);
  };

  // Invoice status options for filtering (based on actual SPARC API backend values)
  // Removed "Captured" since Paid/Unpaid buttons handle this distinction
  const invoiceStatusList = [
    { name: 'New', value: InvoiceStatus.NEW },
    { name: 'Submitted', value: InvoiceStatus.SUBMITTED },
    { name: 'Canceled', value: InvoiceStatus.CANCELED },
    { name: 'Error', value: InvoiceStatus.ERROR },
    { name: 'Voided', value: InvoiceStatus.VOIDED },
  ];

  // Amount filter values from URL
  const amountValues = ['equal'];
  if (amountOption) {
    amountValues[0] = amountOption;
  }
  if (firstAmount) {
    amountValues[1] = firstAmount;
  }

  // Filter configuration for invoices - separate configs for Paid vs Unpaid
  const getInvoiceFilterConfig = (): IFilterConfig[] => {
    const amountFilter = {
      key: 'amount',
      label: 'Amount',
      type: EFilterConfigType.number,
      data: {
        values: amountValues,
      },
      selected: false,
    };

    const statusFilter = {
      key: 'status',
      label: 'Status',
      type: EFilterConfigType.list,
      data: {
        options: invoiceStatusList,
        values: statusArray,
      },
      selected: statusArray?.length > 0,
    };

    // For Paid tab: only amount filter (since only captured status exists)
    // For Unpaid tab: both amount and status filters
    if (paymentStatusFilter === PaymentStatusFilter.PAID) {
      return [amountFilter];
    } else {
      return [amountFilter, statusFilter];
    }
  };

  const filterConfig: IFilterConfig[] = getInvoiceFilterConfig();
  const dateFilterConfig: IFilterConfig = {
    key: 'dates',
    label: 'Create Date',
    type: EFilterConfigType.date,
    data: {
      values: [dateFrom, dateTo],
    },
    selected: false,
  };
  const exportFilterConfig = [dateFilterConfig].concat(filterConfig);

  // Define table columns matching the expected structure from your specification
  const columns: ColumnConfig<IInvoiceData>[] = [
    {
      key: 'create_date',
      label: 'CREATE DATE',
      sortable: true,
      isDate: true,
      renderCell: (row) => (
        <>
          {row.create_date
            ? formatToTz(row.create_date, settings?.timezone, 'MMM dd, hh:mm a')
            : ''}
        </>
      ),
    },
    {
      key: 'original_amount',
      label: 'GROSS AMOUNT',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.convertToMainUnit(getGrossAmount(row))}
        </div>
      ),
    },
    {
      key: 'fee_amount',
      label: 'CONVENIENCE FEE',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.convertToMainUnit(row.fee_amount)}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      sortable: true,
      renderCell: (row) => {
        return invoiceStatusFormatter(row.status);
      },
    },
    {
      key: 'method',
      label: 'PAYMENT TYPE',
      sortable: true,
      renderCell: (row) => <>{normalizePaymentMethodDisplay(row.method)}</>,
    },
    {
      key: 'invoice_number',
      label: 'INVOICE ID',
      sortable: true,
      renderCell: (row) => <div>{row.invoice_number || 'N/A'}</div>,
    },
    {
      key: 'ticket_alpha_num_id',
      label: 'TICKET ID',
      sortable: true,
      renderCell: (row) => (
        <div>{row.ticket_alpha_num_id || row.reference_number || 'N/A'}</div>
      ),
    },
    {
      key: 'actions',
      label: '',
      classNameCell: 'overflow-visible',
      sortable: false,
      renderCell: (row) => {
        // Determine which actions are available based on invoice status
        const status = row.status?.toLowerCase();
        const canCancel =
          status === 'new' || status === 'created' || status === 'submitted';
        const canCapture =
          status === 'new' || status === 'created' || status === 'submitted';
        const hasAnyActions = canCancel || canCapture;

        // Don't show dropdown if no actions are available
        if (!hasAnyActions) {
          return null;
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  style={{ padding: '8px', borderRadius: '4px' }}
                  className="inline-flex items-center justify-between"
                >
                  <Icon name="dots" size={18} className="text-gray-blue-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="p-2.5 dropdown-menu-lg"
              >
                {canCancel && (
                  <DropdownMenuItem
                    className={cn(
                      'cursor-pointer block items-center justify-start font-normal p-1 h-auto',
                      row?.status?.toLowerCase() !== InvoiceStatus.SUBMITTED &&
                        'opacity-50 cursor-not-allowed pointer-events-none'
                    )}
                    disabled={
                      row?.status?.toLowerCase() !== InvoiceStatus.SUBMITTED
                    }
                    onClick={() => handleCancelInvoice(row)}
                  >
                    <div className="flex items-center text-gray-blue-80">
                      <span className="flex items-center text-sm leading-5 text-gray-blue-80">
                        <Icon
                          name="x-circle"
                          size={16}
                          className="mr-1.5 text-gray-blue-60 flex"
                        />
                        Cancel
                      </span>
                    </div>
                    <span className="mt-1 whitespace-normal block self-center ml-5.5 text-xs leading-4 text-[#666e7a]">
                      Cancel this invoice and void the payment request.
                    </span>
                  </DropdownMenuItem>
                )}
                {canCapture && (
                  <DropdownMenuItem
                    className={cn(
                      'cursor-pointer block items-center justify-start font-normal p-1 h-auto',
                      row?.status?.toLowerCase() !== InvoiceStatus.SUBMITTED &&
                        'opacity-50 cursor-not-allowed pointer-events-none'
                    )}
                    disabled={
                      row?.status?.toLowerCase() !== InvoiceStatus.SUBMITTED
                    }
                    onClick={() => handleCaptureInvoice(row)}
                  >
                    <div className="flex items-center text-gray-blue-80">
                      <span className="flex items-center text-sm leading-5 text-gray-blue-80">
                        <Icon
                          name="check-circle"
                          size={16}
                          className="mr-1.5 text-gray-blue-60 flex"
                        />
                        Capture
                      </span>
                    </div>
                    <span className="mt-1 whitespace-normal block self-center ml-5.5 text-xs leading-4 text-[#666e7a]">
                      Capture payment for this invoice and mark as completed.
                    </span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex mb-3">
        <span className="mr-auto">
          <Search queryParams={location?.search} placeHolders="Search invoices..." />
        </span>

        {filterConfig && (
          <>
            <div className="relative flex mr-3 p-0.5 rounded-md bg-gray-blue-5 border border-gray-blue-10 h-9 items-center">
              {/* Animated sliding pill */}
              <div
                className={cn(
                  'absolute inset-y-0.5 w-1/2 rounded bg-white shadow-sm border border-gray-blue-10 transition-all duration-200 ease-out',
                  paymentStatusFilter === PaymentStatusFilter.PAID
                    ? 'left-1/2 -ml-0.5'
                    : 'left-0.5'
                )}
              />
              <Button
                variant="ghost"
                size="sm"
                rounded="none"
                className={cn(
                  'relative z-10 px-4 h-8 font-medium rounded bg-transparent hover:bg-transparent transition-colors duration-200',
                  paymentStatusFilter === PaymentStatusFilter.UNPAID
                    ? 'text-gray-blue-80'
                    : 'text-gray-blue-40 hover:text-gray-blue-60'
                )}
                onClick={() => handleTabSwitch(PaymentStatusFilter.UNPAID)}
              >
                Unpaid
              </Button>
              <Button
                variant="ghost"
                size="sm"
                rounded="none"
                className={cn(
                  'relative z-10 px-4 h-8 font-medium rounded bg-transparent hover:bg-transparent transition-colors duration-200',
                  paymentStatusFilter === PaymentStatusFilter.PAID
                    ? 'text-gray-blue-80'
                    : 'text-gray-blue-40 hover:text-gray-blue-60'
                )}
                onClick={() => handleTabSwitch(PaymentStatusFilter.PAID)}
              >
                Paid
              </Button>
            </div>
            <Filter config={filterConfig} queryParams={location?.search} />
            <ExportBtn onClick={() => setShowExportModal(true)} />
          </>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-3" role="alert">
          Error loading invoices: {error}
        </div>
      )}

      <div className="mb-4">
        <Table
          columns={columns}
          data={invoices}
          isLoading={loading}
          noDataIndication={{
            heading: 'No invoices found',
            subText: 'No invoices found for the selected date range',
          }}
        />
      </div>

      {numResults > 0 && (
        <Pagination
          prevPage={prevPage}
          nextPage={nextPage}
          prevKey={prevKey}
          nextKey={nextKey}
          results={numResults}
        />
      )}

      {showExportModal && (
        <ExportModal
          showModal={showExportModal}
          setShowModal={setShowExportModal}
          config={exportFilterConfig}
          heading="Invoice Export"
          onExport={handleInvoiceExport}
          reportUrl={url || ''}
          exportResponse={exportResponse}
          label="Invoices"
          errorMessage={paymentError?.addUrl?.message || undefined}
          toClearExport={toClearTransactionsExport}
        />
      )}

      <ToastContainer
        type={toastType}
        showToast={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        id="invoice-operation-toast"
        showCross={true}
      />
    </>
  );
};

export default InvoicesTab;
